/**
 * User dashboard Home bundle: one fetch per UTC 6-hour window, per-user localStorage.
 * Windows: 00:00–06:00, 06:00–12:00, 12:00–18:00, 18:00–24:00 (all UTC).
 * After login, sessionStorage forces one network fetch (then slot rules apply).
 */

const STORAGE_PREFIX = 'dashboardHomeBundle_v3_';
const SESSION_FORCE_KEY = 'userDashboardHomeForceNetwork';

/**
 * Start of the current UTC 6-hour window (00, 06, 12, or 18 UTC).
 * @param {number} [nowMs]
 * @returns {number} UTC epoch ms at that slot boundary
 */
export function getCurrentUtcSixHourSlotStartMs(nowMs = Date.now()) {
  const d = new Date(nowMs);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const h = d.getUTCHours();
  const slotHour = Math.floor(h / 6) * 6;
  return Date.UTC(y, m, day, slotHour, 0, 0, 0);
}

/** @deprecated Use UTC slots; kept for any external imports. */
export const DASHBOARD_HOME_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

function isCacheValidForCurrentUtcSlot(o) {
  if (!o || typeof o.utcSlotStartMs !== 'number') return false;
  return o.utcSlotStartMs === getCurrentUtcSixHourSlotStartMs();
}

function readDashboardStoragePayload(userId) {
  if (userId == null || !Number.isFinite(Number(userId))) return null;
  try {
    const s = localStorage.getItem(STORAGE_PREFIX + userId);
    if (!s) return null;
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export function readHomeCache(userId) {
  if (userId == null || !Number.isFinite(Number(userId))) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o.t !== 'number') return null;
    if (!isCacheValidForCurrentUtcSlot(o)) return null;
    return {
      selfInfo: o.selfInfo ?? null,
      referrals: Array.isArray(o.referrals) ? o.referrals : [],
      fetchedAt: o.t,
      utcSlotStartMs: o.utcSlotStartMs,
      raw: o.raw && typeof o.raw === 'object' ? o.raw : null,
    };
  } catch {
    return null;
  }
}

/**
 * Merge fields into `raw` for the current UTC slot (e.g. My Summary fetches after Home).
 * Creates a slot entry if none exists yet. Does not check session "force" — callers use after a fresh network response.
 *
 * Optional keys: walletBalance, paymentSummary, isChild, ledgerTodayTokens, etc.
 */
export function mergeDashboardRawFields(userId, partialRaw) {
  if (userId == null || !Number.isFinite(Number(userId))) return;
  if (!partialRaw || typeof partialRaw !== 'object') return;
  try {
    const slotMs = getCurrentUtcSixHourSlotStartMs();
    const o = readDashboardStoragePayload(userId);
    if (o && isCacheValidForCurrentUtcSlot(o)) {
      const next = {
        ...o,
        t: Date.now(),
        raw: { ...(o.raw && typeof o.raw === 'object' ? o.raw : {}), ...partialRaw },
      };
      localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(next));
      return;
    }
    localStorage.setItem(
      STORAGE_PREFIX + userId,
      JSON.stringify({
        t: Date.now(),
        utcSlotStartMs: slotMs,
        selfInfo: null,
        referrals: [],
        raw: partialRaw,
      })
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * Shallow-merge fields into cached selfInfo (e.g. totalDreameyDiamonds after app-coins refresh).
 */
export function mergeHomeCacheSelfInfo(userId, partialSelfInfo) {
  if (userId == null || !Number.isFinite(Number(userId))) return;
  if (!partialSelfInfo || typeof partialSelfInfo !== 'object') return;
  try {
    const o = readDashboardStoragePayload(userId);
    if (!o || !isCacheValidForCurrentUtcSlot(o)) return;
    const prev = o.selfInfo && typeof o.selfInfo === 'object' ? o.selfInfo : {};
    const next = {
      ...o,
      t: Date.now(),
      selfInfo: { ...prev, ...partialSelfInfo },
    };
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(next));
  } catch {
    /* quota / private mode */
  }
}

/**
 * @param {object|null} [raw] - API payloads shared with My Summary / My Activity (optional)
 */
export function writeHomeCache(userId, selfInfo, referrals, raw = null) {
  if (userId == null || !Number.isFinite(Number(userId))) return;
  try {
    const slotMs = getCurrentUtcSixHourSlotStartMs();
    const existing = readDashboardStoragePayload(userId);
    let mergedRaw = raw && typeof raw === 'object' ? { ...raw } : {};
    if (existing && isCacheValidForCurrentUtcSlot(existing) && existing.raw && typeof existing.raw === 'object') {
      mergedRaw = { ...existing.raw, ...mergedRaw };
    }
    const payload = {
      t: Date.now(),
      utcSlotStartMs: slotMs,
      selfInfo,
      referrals: Array.isArray(referrals) ? referrals : [],
    };
    if (Object.keys(mergedRaw).length > 0) {
      payload.raw = mergedRaw;
    }
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

/**
 * Full dashboard bundle for reuse on other pages (same UTC slot, invalidated by login force flag).
 */
export function getDashboardRawIfFresh(userId) {
  if (shouldForceUserDashboardHomeNetwork()) return null;
  const c = readHomeCache(userId);
  return c?.raw ?? null;
}

/** Call after successful login so the next Home load fetches fresh data for that account. */
export function markUserDashboardHomeRequiresFreshFetch() {
  try {
    sessionStorage.setItem(SESSION_FORCE_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function shouldForceUserDashboardHomeNetwork() {
  try {
    return sessionStorage.getItem(SESSION_FORCE_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearUserDashboardHomeForceNetwork() {
  try {
    sessionStorage.removeItem(SESSION_FORCE_KEY);
  } catch {
    /* ignore */
  }
}
